// src/features/availability/lib/time.ts

/**
 * End-of-day sentinel matching the BE schema. Times in this module accept
 * either an HH:MM string aligned to 15-min boundaries or this sentinel.
 *
 * Mirrors `endTimeRegex` in elixaa-core/elixaa-api/src/schemas/availabilitySchema.ts.
 */
export const END_OF_DAY = '24:00' as const;

const HHMM_15MIN = /^([01]\d|2[0-3]):(00|15|30|45)$/;

export function timeToMinutes(time: string): number {
  if (time === END_OF_DAY) return 1440;
  if (!HHMM_15MIN.test(time)) {
    throw new Error(`Invalid time: ${time}. Expected HH:MM aligned to :00/:15/:30/:45 or "24:00".`);
  }
  const parts = time.split(':').map(Number);
  const h = parts[0];
  const m = parts[1];
  if (h === undefined || m === undefined) {
    throw new Error(`Invalid time format: ${time}`);
  }
  return h * 60 + m;
}

export function minutesToTime(minutes: number): string {
  if (minutes < 0 || minutes > 1440) {
    throw new Error(`Minutes out of range: ${String(minutes)}. Expected 0..1440.`);
  }
  if (minutes === 1440) return END_OF_DAY;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const hStr = h.toString().padStart(2, '0');
  const mStr = m.toString().padStart(2, '0');
  return `${hStr}:${mStr}`;
}

export function isAlignedToGrid(time: string): boolean {
  if (time === END_OF_DAY) return true;
  return HHMM_15MIN.test(time);
}

export function snapToGrid(time: string): string {
  if (time === END_OF_DAY) return time;
  const match = /^(\d{2}):(\d{2})$/.exec(time);
  if (!match) throw new Error(`Cannot snap malformed time: ${time}`);
  const h = match[1];
  const m = match[2];
  if (h === undefined || m === undefined) throw new Error(`Cannot snap malformed time: ${time}`);
  const total = parseInt(h, 10) * 60 + parseInt(m, 10);
  const snapped = Math.min(1425, Math.round(total / 15) * 15);
  return minutesToTime(snapped);
}
